import _ from 'lodash';
import {activeParticipantChanged} from '../../../store/settings/actions';
import {ActiveParticipantService} from '../../../services/participants/active-participant.service';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IParticipant, IParticipantConfig} from '@interfaces/interfaces';
import {JSONSchema7} from 'json-schema';
import {participantPages} from '../participants.routes';
import {ParticipantService} from '../../../services/participants/participant.service';
import {ProjectDto} from '@api/projects/interfaces';
import {ProjectGuardService} from '../../../components/cleanui/system/Guard/project.guard';
import {ProjectService} from '../../../services/project/project.service';
import {Router} from '@angular/router';
import {SessionItemData} from '../../../workflow/services/session-item-data';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {SplitButtonElement} from '../../../components/elements/splitbutton/splitbutton.component';
import {InterpolationService, TextInterpolation} from '../../../services/interpolation/interpolation.service';
import { MenuService } from '@app/services/menu';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@app/services/error-notification/error-notification.service';
import { FormSchemaRendererComponent } from '@app/components/renderer/form-schema-renderer.component';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { PermissionService } from '@app/services/permission/permission.service';

@Component({
	selector: 'app-participant-edit',
	templateUrl: './participant-edit.component.html',
	styles: [`
:host{
	display: block;
	overflow-x: hidden;
	overflow-y: auto;
	flex: 1 1 auto;
}`],
	providers: [ProjectGuardService],
})
export class ParticipantEditComponent implements OnInit, OnDestroy {
	private subscription = new Subscription();
	private participant: IParticipant;
	private project: ProjectDto;
	private participantInfoTemplate: TextInterpolation;
	private titleSub: Subscription;

	@ViewChild(FormSchemaRendererComponent)
	private form: FormSchemaRendererComponent;

	data: SessionItemData = null;
	participantInfo: string = null;
	resolvedSchema: JSONSchema7 = null;

	participantFormID = 'form-edit-participant';

	public userEditSplitButton: SplitButtonElement = {
		title: 'participants.edit',
		icon: 'save',
		type: 'primary',
		buttonType: 'submit',
		attrForm: this.participantFormID,
	};
	public userCancelSplitButton: SplitButtonElement = {
		title: 'participants.cancel',
		icon: 'x',
		type: 'default',
		callback: () => this.navigateToInfoPage(),
	};
	public userDeleteSplitButton: SplitButtonElement = {
		title: 'actions.delete',
		icon: 'trash',
		type: 'default',
		callback: () => this.openDeleteUserModal(),
	};

	public deleteUserModalVisible = false;
	public canDelete$: Observable<boolean>;
	public canEdit$: Observable<boolean>;
	private canEdit: boolean;

	constructor(
		protected projectGuardService: ProjectGuardService,
		private activeParticipantService: ActiveParticipantService,
		private participantService: ParticipantService,
		private projectService: ProjectService,
		private router: Router,
		private store: Store,
		private notification: NzNotificationService,
		private interpolationService: InterpolationService,
		private menuService: MenuService,
		private translateService: TranslateService,
		private errorService: ErrorNotificationService,
		permissionService: PermissionService,
	) {
		this.canDelete$ = permissionService.hasProjectPermission$('participants:delete');
		this.canEdit$ = permissionService.hasProjectPermission$('participants:write');
	}

	ngOnInit(): void {
		this.subscription.add(
			this.canDelete$.subscribe(canDelete => this.userDeleteSplitButton.disabled = !canDelete)
		);
		this.subscription.add(
			this.canEdit$.subscribe(canEdit => {
				this.userEditSplitButton.disabled = !canEdit;
				this.canEdit = canEdit;
			})
		);
		this.subscription.add(
			this.activeParticipantService.participant$.subscribe(participant => this.loadParticipant(participant))
		);
		this.subscription.add(
			this.participantService.resolvedParticipantsConfigSchema$.subscribe(resolvedSchema => {
				this.resolvedSchema = resolvedSchema;
			})
		);
		this.subscription.add(
			this.projectService.activeProject$.subscribe(project => this.project = project)
		);
		this.subscription.add(
			this.projectService.participantsConfig$.subscribe(config => {
				this.loadParticipantDetailInfos(config);
			})
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private loadParticipant(participant: IParticipant): void {
		this.participant = _.cloneDeep(participant);
		this.data = new SessionItemData(this.participant?.data);
		this.participantInfo = this.participantInfoTemplate?.interpolate(participant.data) || participant.id;
	}

	async editParticipant(isFormValid: boolean): Promise<void> {
		if (!isFormValid) {
			return;
		}

		try {
			this.participant.data = this.data.data;
			const participant = await this.participantService.updateParticipant(this.project, this.participant);
			this.participantService.refreshParticipants();
			this.store.dispatch(activeParticipantChanged({participant : participant}));

			this.navigateToInfoPage();
		} catch (error) {
			this.errorService.showErrorNotification(error);
			const property = error?.error?.property;
			const prefix = 'data.';
			if (typeof property === 'string' && property.startsWith(prefix)) {
				this.form.setValidationError(property.substring(prefix.length), error.error.message);
			}
		}
	}

	public navigateToInfoPage(): void {
		this.router
			.navigate([participantPages.stammDaten.base()])
			.catch(console.error);
	}

	public navigateToParticipantList(): void {
		this.router
			.navigate([participantPages.dashboard.base()])
			.catch(console.error);
	}

	public openDeleteUserModal(): void {
		this.deleteUserModalVisible = true;
	}

	public deleteUser(): void {
		this.participantService.deleteParticipant(this.project, this.participant).then(() => {
			this.notification.warning('', 'Benutzer ' + this.participantInfo + ' erfolgreich gelöscht.');
			this.closeDeleteUserModal();
			this.navigateToParticipantList();
		}).catch(error => {
			this.errorService.showErrorNotification(error);
		});
	}

	public closeDeleteUserModal(): void {
		this.deleteUserModalVisible = false;
	}

	private loadParticipantDetailInfos(config: IParticipantConfig) {
		const template = config?.details?.info?.name;
		this.participantInfoTemplate = template && this.interpolationService.interpolation(template);
		this.participantInfo = this.participantInfoTemplate?.interpolate(this.participant.data) || this.participant.id;
		this.subscription.add(this.translateService.stream('actions.edit.item', { item: this.participantInfo }).subscribe(
			result => this.menuService.title = result));
	}

	async setSubmitActive(active: boolean): Promise<void> {
		this.userEditSplitButton.disabled = !active || !this.canEdit;
	}
}
